import { Box, Button, Card, Group, Modal, Select, Stack, Text, TextInput, Textarea, Title } from '@mantine/core';
import { formatDateTime, normalizeErrorString } from '@medplum/core';
import { Patient, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Document, useMedplum } from '@medplum/react';
import { IconCircleCheck, IconCircleOff, IconEdit, IconShare, IconTrash } from '@tabler/icons-react';
import { useState, type JSX } from 'react';
import { showNotification } from '@mantine/notifications';

interface JournalEntry {
  id?: string;
  title: string;
  content: string;
  mood: string;
  tags: string[];
  shareWithTherapist: boolean;
  createdAt: string;
}

const moodOptions = [
  { value: 'happy', label: 'Happy' },
  { value: 'calm', label: 'Calm' },
  { value: 'anxious', label: 'Anxious' },
  { value: 'sad', label: 'Sad' },
  { value: 'angry', label: 'Angry' },
  { value: 'neutral', label: 'Neutral' },
];

export function Journal(): JSX.Element {
  const medplum = useMedplum();
  const profile = medplum.getProfile() as Patient;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMood, setSelectedMood] = useState<string | null>(null);

  // Fetch journal entries
  const responses = medplum
    .searchResources('QuestionnaireResponse', `subject=${profile.id}&questionnaire=journal-entry`)
    .read();

  // Convert QuestionnaireResponses to JournalEntry format
  const journalEntries: JournalEntry[] = responses.map((response) => ({
    id: response.id,
    title: response.item?.find((i) => i.linkId === 'title')?.answer?.[0]?.valueString || '',
    content: response.item?.find((i) => i.linkId === 'content')?.answer?.[0]?.valueString || '',
    mood: response.item?.find((i) => i.linkId === 'mood')?.answer?.[0]?.valueString || '',
    tags: response.item?.find((i) => i.linkId === 'tags')?.answer?.[0]?.valueString?.split(',') || [],
    shareWithTherapist: response.item?.find((i) => i.linkId === 'share')?.answer?.[0]?.valueBoolean || false,
    createdAt: response.authored || '',
  }));

  const filteredEntries = journalEntries.filter((entry) => {
    const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMood = !selectedMood || entry.mood === selectedMood;
    return matchesSearch && matchesMood;
  });

  const handleSave = async (entry: JournalEntry): Promise<void> => {
    try {
      const response: QuestionnaireResponse = {
        resourceType: 'QuestionnaireResponse',
        questionnaire: 'journal-entry',
        status: 'completed',
        subject: { reference: `Patient/${profile.id}` },
        authored: new Date().toISOString(),
        item: [
          {
            linkId: 'title',
            answer: [{ valueString: entry.title }],
          },
          {
            linkId: 'content',
            answer: [{ valueString: entry.content }],
          },
          {
            linkId: 'mood',
            answer: [{ valueString: entry.mood }],
          },
          {
            linkId: 'tags',
            answer: [{ valueString: entry.tags.join(',') }],
          },
          {
            linkId: 'share',
            answer: [{ valueBoolean: entry.shareWithTherapist }],
          },
        ],
      };

      if (editingEntry?.id) {
        await medplum.updateResource({ ...response, id: editingEntry.id });
      } else {
        await medplum.createResource(response);
      }

      showNotification({
        icon: <IconCircleCheck />,
        title: 'Success',
        message: 'Journal entry saved',
      });
      setModalOpen(false);
      setEditingEntry(null);
      window.location.reload();
    } catch (error) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(error),
      });
    }
  };

  const handleDelete = async (entryId: string): Promise<void> => {
    try {
      await medplum.deleteResource('QuestionnaireResponse', entryId);
      showNotification({
        icon: <IconCircleCheck />,
        title: 'Success',
        message: 'Journal entry deleted',
      });
      window.location.reload();
    } catch (error) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(error),
      });
    }
  };

  return (
    <Document>
      <Group justify="space-between" mb="xl">
        <Title>Journal</Title>
        <Button onClick={() => setModalOpen(true)}>New Entry</Button>
      </Group>

      <Group mb="xl">
        <TextInput
          placeholder="Search entries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />
        <Select
          placeholder="Filter by mood"
          value={selectedMood}
          onChange={setSelectedMood}
          data={moodOptions}
          clearable
        />
      </Group>

      <Stack>
        {filteredEntries.map((entry) => (
          <Card key={entry.id} withBorder shadow="sm">
            <Group justify="space-between" mb="md">
              <div>
                <Title order={3}>{entry.title}</Title>
                <Text size="sm" c="dimmed">
                  {formatDateTime(entry.createdAt)} • Mood: {entry.mood}
                </Text>
              </div>
              <Group>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => {
                    setEditingEntry(entry);
                    setModalOpen(true);
                  }}
                >
                  <IconEdit size={16} />
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  color="red"
                  onClick={() => entry.id && handleDelete(entry.id)}
                >
                  <IconTrash size={16} />
                </Button>
              </Group>
            </Group>
            <Text>{entry.content}</Text>
            {entry.tags.length > 0 && (
              <Group mt="md">
                {entry.tags.map((tag) => (
                  <Text key={tag} size="sm" c="dimmed">
                    #{tag}
                  </Text>
                ))}
              </Group>
            )}
            {entry.shareWithTherapist && (
              <Text size="sm" c="dimmed" mt="md">
                <IconShare size={16} style={{ verticalAlign: 'middle' }} /> Shared with therapist
              </Text>
            )}
          </Card>
        ))}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingEntry(null);
        }}
        title={editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}
        size="lg"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const entry: JournalEntry = {
              title: formData.get('title') as string,
              content: formData.get('content') as string,
              mood: formData.get('mood') as string,
              tags: (formData.get('tags') as string).split(',').map((t) => t.trim()).filter(Boolean),
              shareWithTherapist: formData.get('share') === 'on',
              createdAt: new Date().toISOString(),
            };
            handleSave(entry);
          }}
        >
          <Stack>
            <TextInput
              label="Title"
              name="title"
              required
              defaultValue={editingEntry?.title}
            />
            <Textarea
              label="Content"
              name="content"
              minRows={5}
              required
              defaultValue={editingEntry?.content}
            />
            <Select
              label="Mood"
              name="mood"
              data={moodOptions}
              required
              defaultValue={editingEntry?.mood}
            />
            <TextInput
              label="Tags"
              name="tags"
              placeholder="Enter tags separated by commas"
              defaultValue={editingEntry?.tags.join(', ')}
            />
            <Group>
              <input
                type="checkbox"
                name="share"
                id="share"
                defaultChecked={editingEntry?.shareWithTherapist}
              />
              <label htmlFor="share">Share with therapist</label>
            </Group>
            <Group justify="flex-end">
              <Button
                variant="subtle"
                onClick={() => {
                  setModalOpen(false);
                  setEditingEntry(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Document>
  );
}